import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { EndorsementType } from "@ti/core";
import { CircleCheck, CircleX, ScrollText } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Hash } from "@/components/domain/code";
import { ErrorPanel } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { EndorsementTimeline, buildChain, chainIntegrity } from "@/features/coverage/endorsement-timeline";
import { FilterPill } from "@/features/coverage/filter-pill";
import { ENDORSEMENT_META, ENDORSEMENT_TYPES } from "@/features/coverage/meta";
import { q } from "@/lib/api";

interface EndorsementSearch {
  type?: EndorsementType;
  focus?: string;
  policyId?: string;
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

export const Route = createFileRoute("/_app/coverage/endorsements")({
  validateSearch: (s: Record<string, unknown>): EndorsementSearch => ({
    type: typeof s.type === "string" && (ENDORSEMENT_TYPES as string[]).includes(s.type) ? (s.type as EndorsementType) : undefined,
    focus: str(s.focus),
    policyId: str(s.policyId),
  }),
  component: EndorsementsPage,
});

function EndorsementsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // Always load the full chain: integrity is verified across every policy, filters only narrow the view.
  const endorsements = useQuery(q.endorsements());
  const agents = useQuery(q.agents());
  const policies = useQuery(q.policies());
  const agentName = useMemo(() => new Map((agents.data ?? []).map((a) => [a.id, a.name])), [agents.data]);
  const policyName = useMemo(() => new Map((policies.data ?? []).map((p) => [p.id, p.name])), [policies.data]);

  const chain = useMemo(() => buildChain(endorsements.data), [endorsements.data]);
  const scoped = search.policyId ? chain.filter((c) => c.e.policyId === search.policyId) : chain;

  const counts = useMemo(() => {
    const c = new Map<EndorsementType, number>();
    for (const { e } of scoped) c.set(e.type, (c.get(e.type) ?? 0) + 1);
    return c;
  }, [scoped]);

  const visible = search.type ? scoped.filter((c) => c.e.type === search.type) : scoped;
  const { links, verified, broken } = chainIntegrity(chain);
  const head = chain[0]?.e;

  const setSearch = (patch: Partial<EndorsementSearch>) => void navigate({ search: (prev: EndorsementSearch) => ({ ...prev, ...patch }), replace: true });

  useEffect(() => {
    if (!search.focus || !chain.length) return;
    const id = requestAnimationFrame(() => document.getElementById(`end-${search.focus}`)?.scrollIntoView({ block: "center" }));
    return () => cancelAnimationFrame(id);
  }, [search.focus, chain.length]);

  if (endorsements.error) return <ErrorPanel error={endorsements.error} reset={() => void endorsements.refetch()} />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-wrap items-center gap-x-8 gap-y-3 py-3">
          <div>
            <div className="text-xs text-fg-3">{search.policyId ? "On this policy" : "Endorsements"}</div>
            <div className="mt-0.5 text-lg font-semibold tabular">
              {endorsements.isPending ? "—" : scoped.length}
              {search.policyId ? <span className="ml-1 text-xs font-normal text-fg-3">of {chain.length}</span> : null}
            </div>
          </div>
          <div>
            <div className="text-xs text-fg-3">Chain integrity (all policies)</div>
            <div className="mt-1">
              {endorsements.isPending ? (
                <Skeleton className="h-5 w-40" />
              ) : broken === 0 ? (
                <Badge tone="good">
                  <CircleCheck /> {verified} of {links} links verified
                </Badge>
              ) : (
                <Badge tone="critical">
                  <CircleX /> {broken} broken {broken === 1 ? "link" : "links"}
                </Badge>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-fg-3">Chain head</div>
            <div className="mt-1">{head ? <Hash value={head.hash} n={16} /> : <Skeleton className="h-5 w-40" />}</div>
          </div>
          <p className="max-w-md text-xs text-fg-3 lg:ml-auto">
            Every coverage change on any of your policies is an immutable endorsement. Each record carries the hash of the one before it, so any edit to history breaks the chain.
          </p>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Filter by endorsement type" className="flex flex-wrap items-center gap-1">
          <FilterPill active={!search.type} label="All types" count={scoped.length} onClick={() => setSearch({ type: undefined })} />
          {ENDORSEMENT_TYPES.filter((t) => counts.has(t)).map((t) => (
            <FilterPill
              key={t}
              active={search.type === t}
              icon={ENDORSEMENT_META[t].icon}
              label={ENDORSEMENT_META[t].label}
              count={counts.get(t) ?? 0}
              onClick={() => setSearch({ type: search.type === t ? undefined : t })}
            />
          ))}
        </div>
        <Select aria-label="Filter by policy" value={search.policyId ?? ""} onChange={(e) => setSearch({ policyId: e.target.value || undefined, type: undefined })} className="ml-auto w-60">
          <option value="">All policies</option>
          {policies.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>

      {endorsements.isPending ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="ml-[148px] h-28" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState icon={<ScrollText />} title="No endorsements match" description="Clear the type or policy filter to see the full chain." />
        </Card>
      ) : (
        <EndorsementTimeline
          items={visible}
          agentName={agentName}
          policyName={policyName}
          filtered={Boolean(search.type || search.policyId)}
          focus={search.focus}
          showPolicy={!search.policyId}
        />
      )}
    </div>
  );
}
